
import { processAPIData } from './processor';
import { TimePeriod } from './types';
import { debugLog, debugWarn } from '@/utils/debugLogger';

// Server endpoints - il client ora chiama il server invece dell'API esterna
const SERVER_BASE_URL = typeof window !== 'undefined' ? window.location.origin : '';

async function fetchFromServer(endpoint: string): Promise<any> {
  if (typeof window === 'undefined') {
    throw new Error('fetchFromServer called in a non-browser context');
  }

  const url = `${SERVER_BASE_URL}/api/metal-prices/${endpoint}`;

  try {
    debugLog('pricing', `Fetching from server: ${url}`, undefined, 'api');
    const response = await fetch(url);
    
    if (!response.ok) {
      const errorBody = await response.text();
      throw new Error(`Server request failed with status ${response.status}: ${errorBody}`);
    }
    
    // Check content type to ensure we got JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const textBody = await response.text();
      debugWarn('pricing', `Server returned non-JSON response. Content-Type: ${contentType}`, textBody, 'api');
      throw new Error('Server returned non-JSON response (possibly HTML error page)');
    }
    
    const result = await response.json();
    debugLog('pricing', 'Server response received successfully', undefined, 'api');
    
    if (!result.success) {
      throw new Error(`Server error: ${result.error || 'Unknown error'}`);
    }
    
    return result.data;
  } catch (error) {
    debugWarn('pricing', 'Error in fetchFromServer (transient, will retry):', error, 'api');
    
    // If JSON parsing failed, provide more specific error
    if (error instanceof SyntaxError && error.message.includes('JSON')) {
      throw new Error('Server returned invalid JSON response - API endpoint may be unavailable');
    }
    
    throw error;
  }
}

export async function fetchSpotPricesFromAPI() {
  const rawData = await fetchFromServer('spot');
  return processAPIData(rawData, 'spot');
}

export const fetchCurrentPrices = fetchSpotPricesFromAPI;

export async function fetchFixingPricesFromAPI() {
  const rawData = await fetchFromServer('fixing');
  return processAPIData(rawData, 'fixing');
}

const periodMapping: Record<TimePeriod, string> = {
  week: 'weekly',
  month: 'monthly',
  year: 'yearly',
  all: 'all',
};

export async function fetchHistoryFromAPI(period: TimePeriod) {
  const serverPeriod = periodMapping[period] || 'weekly'; // Fallback to 'weekly'
  const rawData = await fetchFromServer(`historical/${serverPeriod}`);
  return processAPIData(rawData, 'storico');
}
